export default {
  en: {
    avg: 'Average',
    sum: 'Summation',
    total: 'Total',
    filterHint: 'You can filter the metrics for a more granular view.',
    segmentPlaceholder: 'Filter by segment',
    agentPlaceholder: 'Filter by agent',
    tagsPlaceholder: 'Filter by tags',
    formPlaceholder: 'Filter by form',
    formTypePlaceholder: 'Filter by form type',
    emptyPeriod: 'The selected period does not contain data',
    others: 'Others ({count})',
    entries: 'entries',
    rangeLimit: 'This view is limited to {days} days. Narrow the selected date range to load the data.',
    indicators: {
      title: 'Indicators',
      subtitle: 'Track each metric against its target',
      panel: 'Panel',
      manage: 'Manage',
      condition: 'Condition',
      recommendedWarning: 'Some recommended indicators are not created yet - create them to track them:',
      threshold: 'Threshold',
      trend: 'Trend',
      noHistory: 'No history yet - it will fill in with the daily snapshot.',
      actualSeries: 'Actual',
      targetSeries: 'Target',
      new: 'New indicator',
      edit: 'Edit indicator',
      empty: 'No indicators yet',
      emptyHint: 'Create an indicator to contrast a metric against its target.',
      name: 'Name',
      metric: 'Metric',
      operator: 'Condition',
      targetLabel: 'Target',
      filtersLabel: 'Filters (optional)',
      segments: 'Segments',
      services: 'Calendars',
      agents: 'Agents',
      enabled: 'Enabled',
      actual: 'Actual',
      met: 'On target',
      notMet: 'Off target',
      deviation: 'Deviation',
      deleteConfirm: 'Delete this indicator?',
      saved: 'Indicator saved',
      removed: 'Indicator deleted',
      operators: { lt: 'less than', lte: 'at most', gt: 'greater than', gte: 'at least', eq: 'equal to', between: 'between' },
    },
    emptyTitle: 'No data found',
    queueDuration: 'Time in the queue',
    emptyComments: 'No comments',
    aproxLocation: 'Approximate location',
    errorCallPreviewMessage: 'The call is in progress',
    errorCallPreviewDescription: 'You must wait for the call to be answered by an agent before viewing it',
    formType: 'Form type',
    globalSearch: 'Global search',
    available: 'Availables',
    selected: 'Selected',
    calls: {
      title: 'Call average duration',
      hint: 'View the average duration of calls in the selected period',
      answeredTitle: 'Answered calls',
      answeredHint: 'Total calls answered in the selected period',
      abandonedTitle: 'Abandoned calls',
      abandonedHint: 'Total calls abandoned in the selected period',
      transferredTitle: 'Transferred calls',
      transferredHint: 'Total calls transferred in the selected period',
      rejectedTitle: 'Rejected calls',
      rejectedHint: 'Total calls rejected by each agent in the selected period',
      unansweredTitle: 'Unanswered assignments',
      unansweredHint: 'Times each agent did not answer an assigned call before the ACD moved on, in the selected period',
      answeredShare: 'Of all calls answered by the team this period, the share this agent handled.',
      transferredShare: 'Of all transfers made by the team this period, the share this agent made.',
      rejectedShare: 'Of all rejections made by the team this period, the share this agent made.',
      unansweredShare: 'Of all assignments the team left unanswered this period, this agent\'s share.',
      avgMinutesTitle: 'Average minutes',
      avgMinutesHint: 'View average call duration per segment',
      surveyPlaceholder: 'Select a survey',
      surveyFieldPlaceholder: 'Select a field of the survey',
      hour: {
        label: 'Hour volume',
        hint: 'Crossover of answered, abandoned, transferred and rejected calls',
      },
      week: {
        label: 'Week volume',
        hint: 'Crossover of answered, abandoned, transferred and rejected calls'
      },
      month: {
        label: 'Month volume',
        hint: 'Crossover of answered, abandoned, transferred and rejected calls',
      },
      distribution: {
        label: 'Status distribution',
        hint: 'Share of calls by status in the selected period',
      }
    },
    queues: {
      title: 'Queue average duration',
      hint: 'View the average wait in the selected period',
    },
    agents: {
      metricsTitle: 'Executive metrics',
      metricsHint: 'AHT, occupancy and time-by-status per executive',
      avg: {
        label: 'Average duration call by agent',
        hint: 'Check the average duration of calls by agent'
      },
      survey: {
        label: 'Survey score by agent',
        hint: 'Get the score by agent based on a survey field'
      },
      aht: {
        title: 'Average handle time by agent',
        hint: 'Average call minutes handled per agent',
        agent: 'Agent',
        avgMinutes: 'Avg. minutes',
      },
      status: {
        title: 'Time by status per agent',
        hint: 'Minutes each agent spent in each status',
        agent: 'Agent',
        values: {
          connected: 'Connected', disconnected: 'Disconnected', online: 'Connected', offline: 'Disconnected',
          available: 'Available', busy: 'Busy', occupied: 'In call', 'in-call': 'In call', incall: 'In call',
          away: 'Away', paused: 'Paused', break: 'Break', ringing: 'Ringing', dialing: 'Dialing', hold: 'On hold',
        },
      },
      occupancy: {
        title: 'Agent occupancy',
        hint: 'Daily occupancy rate (%) per agent',
        date: 'Date',
      },
    },
    sla: {
      asaTargetLabel: 'ASA target',
      callsLabel: 'Calls',
      appointmentsLabel: 'Appointments',
      aht: { title: 'AHT', hint: 'Average Handle Time - average handling time across calls and appointments' },
      frt: { title: 'FRT', hint: 'First Response Time - average wait before the first response' },
      asa: { title: 'ASA', hint: 'Average Speed of Answer - percentage of interactions answered within the target', avg: 'Avg. duration' },
      abandon: { title: 'Abandonment rate', hint: 'Percentage of abandoned queued calls' },
      surveys: {
        nps: 'NPS', npsHint: 'Net Promoter Score average',
        ces: 'CES', cesHint: 'Customer Effort Score average',
        csat: 'CSAT', csatHint: 'Customer Satisfaction average',
        range: 'Range', rangeHint: 'Average of numeric range fields',
      },
    },
    reports: {
      title: 'Global reports',
      hint: 'Aggregated contact-center reports with optional grouping',
      tabs: { calls: 'Calls and queues', appointments: 'Appointments', agents: 'Agents' },
      modes: {
        global: 'Global', segment: 'By segment', service: 'By calendar', time: 'By time',
      },
      groupBy: {
        hour: 'Hour', dayOfWeek: 'Day of week', dayOfYear: 'Day of year', date: 'Date', month: 'Month', year: 'Year',
      },
      columns: {
        totalCalls: 'Total calls', totalAppointments: 'Total appointments', avgWaitingTime: 'Avg. waiting time',
        dismissed: 'Dismissed', canceled: 'Canceled', attended: 'Attended', pending: 'Pending', rescheduled: 'Rescheduled',
        answered: 'Answered', transferred: 'Transferred', abandoned: 'Abandoned', rejected: 'Declined', dropped: 'Dropped',
        totalQueues: 'Total queues',
        avgDuration: 'Avg. duration', totalMinutesInCall: 'Total minutes in calls', totalMinutesInAppointment: 'Total minutes in appointments',
        attendanceRate: 'Attendance rate', tmoCall: 'AHT in calls', tmoAppointment: 'AHT in appointments', user: 'Agent',
        segment: 'Segment', service: 'Calendar',
        hour: 'Hour', date: 'Date', year: 'Year', dayOfWeek: 'Day of week', month: 'Month', dayOfYear: 'Day',
      },
    },
    appointments: {
      filters: { agent: 'Filter by agent', service: 'Filter by calendar' },
      attendanceRate: 'Attendance rate',
      statuses: {
        pending: 'Pending', attended: 'Attended', canceled: 'Canceled', rescheduled: 'Rescheduled',
      },
      volume: {
        label: 'Appointment volume',
        hint: 'Crossover of pending, attended, canceled and rescheduled appointments over the selected grouping',
      },
      distribution: {
        label: 'Status distribution',
        hint: 'Share of appointments by status in the selected period',
      },
    },
    maxRange: 'The maximum allowed range is 31 days',
    overview: {
      occupancy: { title: 'Global occupancy', hint: 'Average occupancy rate (%) across all executives' },
      surveysLabel: 'Surveys',
      topAgents: { title: 'Top agents by answered calls', hint: 'Agents ranked by answered calls in the period' },
      topSegments: { title: 'Top segments by volume', hint: 'Segments ranked by answered calls in the period' },
      sections: { sla: 'SLA', activity: 'Ranking', surveys: 'Surveys' },
      occupancyLimit: 'Max 31 days',
    },
    dayOfWeek: {
      label: 'Group by days of the week',
    },
    dayOfYear: {
      label: 'Days of the year',
    },
    hour: {
      label: 'Group by hours',
    },
    month: {
      label: 'Group by month',
    },
    year: {
      label: 'Group by year',
    },
    surveys: {
      totalByOption: 'Total by option',
      totalByOptionHint: 'Total number of times an option was selected',
      averageHint: 'Average of all values',
      exports: 'Exports surveys',
      exportHint: 'The survey exports has moved to unified Export menu',
      rememberMessage: 'Reminder',
      rememberDescription: 'You must select a survey and field in the filters',
    },
    exports: {
      title: 'Exports to XLSX',
      formId: 'Form id',
      appointmentId: 'Appointment id',
      callId: 'Call id',
      emptyExport: 'No forms found',
      preForm: 'Previous to call',
      contact: 'Contact',
      alertDates: 'The export will be carried out on the dates selected above',
      alertDownload: 'We will download all shipments that match the filters and selected form',
      entityPlaceholder: 'Select the data to export',
      exportTitle: 'Export data',
      securityAlert: 'For security reasons, it is only possible to extract 100.000 records per export',
      unitMesuarement: 'The standard unit of measurement is minutes',
      timezoneAlert: 'Times are in local time zone {timezone}',
      selectFormAgent: 'Select an agent form to export',
      confirmAlert: 'If you want to export the agent forms between the dates {startAt} and {endAt} click below'
    },
    recordings: {
      recordingId: 'Recording Id',
      size: 'Size (MB)',
      fileId: 'File Id',
      externalStorage: 'External storage',
      viewer: 'Recording viewer',
      statusChangeTitle: 'Change recording status',
      successfulDeleted: 'Recording was successfully deleted',
      successfulStatus: 'Status was successfully changed',
      successfulDownload: 'Successfully download',
      hot: 'Hot storage',
      cold: 'Cold storage',
      used: 'Used',
      free: 'Free',
    },
    topics: {
      titleModal: 'Editing topics',
      placeholder: 'Help us improve, tell us why you have added or removed certain topics',
    }
  },
  es: {
    avg: 'Promedio',
    sum: 'Sumatoria',
    total: 'Total',
    filterHint: 'Puedes filtrar las métricas para una vista más detallada.',
    segmentPlaceholder: 'Filtrar por segmento',
    agentPlaceholder: 'Filtrar por agente',
    tagsPlaceholder: 'Filtrar por etiquetas',
    formPlaceholder: 'Filtrar por formulario',
    formTypePlaceholder: 'Filtrar por tipo de formulario',
    emptyPeriod: 'El periodo seleccionado no contiene datos',
    others: 'Otros ({count})',
    entries: 'registros',
    rangeLimit: 'Esta vista está limitada a {days} días. Reduce el rango de fechas seleccionado para cargar los datos.',
    indicators: {
      title: 'Indicadores',
      subtitle: 'Contrasta cada métrica contra su objetivo',
      panel: 'Panel',
      manage: 'Gestión',
      condition: 'Condición',
      recommendedWarning: 'Algunos indicadores recomendados aún no están creados - créalos para monitorearlos:',
      threshold: 'Umbral',
      trend: 'Tendencia',
      noHistory: 'Aún no hay historial - se completará con el snapshot diario.',
      actualSeries: 'Actual',
      targetSeries: 'Objetivo',
      new: 'Nuevo indicador',
      edit: 'Editar indicador',
      empty: 'Aún no hay indicadores',
      emptyHint: 'Crea un indicador para contrastar una métrica con su objetivo.',
      name: 'Nombre',
      metric: 'Métrica',
      operator: 'Condición',
      targetLabel: 'Objetivo',
      filtersLabel: 'Filtros (opcional)',
      segments: 'Segmentos',
      services: 'Calendarios',
      agents: 'Agentes',
      enabled: 'Activo',
      actual: 'Actual',
      met: 'En objetivo',
      notMet: 'Fuera de objetivo',
      deviation: 'Desviación',
      deleteConfirm: '¿Eliminar este indicador?',
      saved: 'Indicador guardado',
      removed: 'Indicador eliminado',
      operators: { lt: 'menor que', lte: 'como máximo', gt: 'mayor que', gte: 'al menos', eq: 'igual a', between: 'entre' },
    },
    emptyTitle: 'No se encontraron datos',
    queueDuration: 'Tiempo en la fila',
    emptyComments: 'Sin comentarios',
    aproxLocation: 'Ubicación aproximada',
    errorCallPreviewMessage: 'La llamada está en progreso',
    errorCallPreviewDescription: 'Debes esperar a que un agente atienda la llamada antes de visualizarla',
    formType: 'Tipo de formulario',
    globalSearch: 'Búsqueda global',
    available: 'Disponibles',
    selected: 'Seleccionados',
    calls: {
      title: 'Duración promedio de la llamada',
      hint: 'Visualiza la duración promedio de las llamadas en el periodo seleccionado',
      answeredTitle: 'Llamadas atendidas',
      answeredHint: 'Total de llamadas atendidas en el periodo seleccionado',
      abandonedTitle: 'Llamadas abandonadas',
      abandonedHint: 'Total de llamadas abandonadas en el periodo seleccionado',
      transferredTitle: 'Llamadas transferidas',
      transferredHint: 'Total de llamadas transferidas en el periodo seleccionado',
      rejectedTitle: 'Llamadas rechazadas',
      rejectedHint: 'Total de llamadas rechazadas por cada ejecutivo en el periodo seleccionado',
      unansweredTitle: 'Asignaciones sin respuesta',
      unansweredHint: 'Veces que cada ejecutivo no contestó una llamada asignada antes de que el ACD reasignara, en el periodo seleccionado',
      answeredShare: 'Del total de llamadas atendidas por el equipo en el periodo, el porcentaje que atendió este ejecutivo.',
      transferredShare: 'Del total de transferencias del equipo en el periodo, el porcentaje que hizo este ejecutivo.',
      rejectedShare: 'Del total de rechazos del equipo en el periodo, el porcentaje que hizo este ejecutivo.',
      unansweredShare: 'Del total de asignaciones sin respuesta del equipo en el periodo, el porcentaje de este ejecutivo.',
      avgMinutesTitle: 'Minutos promedio',
      avgMinutesHint: 'Visualiza la duración promedio de las llamadas por segmento',
      surveyPlaceholder: 'Selecciona una encuesta',
      surveyFieldPlaceholder: 'Selecciona un campo de la encuesta',
      hour: {
        label: 'Volumen por hora',
        hint: 'Cruce de llamadas atendidas, abandonadas, transferidas y rechazadas',
      },
      week: {
        label: 'Volumen por semana',
        hint: 'Cruce de llamadas atendidas, abandonadas, transferidas y rechazadas'
      },
      month: {
        label: 'Volumen por mes',
        hint: 'Cruce de llamadas atendidas, abandonadas, transferidas y rechazadas',
      },
      distribution: {
        label: 'Distribución por estado',
        hint: 'Proporción de llamadas por estado en el periodo seleccionado',
      }
    },
    queues: {
      title: 'Duración promedio en fila',
      hint: 'Visualiza la espera promedio en el periodo seleccionado',
    },
    agents: {
      metricsTitle: 'Métricas por ejecutivo',
      metricsHint: 'AHT, ocupación y tiempo por estado de cada ejecutivo',
      avg: {
        label: 'Duración promedio por agente',
        hint: 'Verifica la duración promedio de las llamadas por agente'
      },
      survey: {
        label: 'Puntuación de encuesta por agente',
        hint: 'Obtener la puntuación por agente basada en un campo de la encuesta'
      },
      aht: {
        title: 'Tiempo medio de gestión por agente',
        hint: 'Minutos promedio de llamada gestionados por agente',
        agent: 'Agente',
        avgMinutes: 'Min. promedio',
      },
      status: {
        title: 'Tiempo por estado por ejecutivo',
        hint: 'Minutos que cada ejecutivo pasó en cada estado',
        agent: 'Ejecutivo',
        values: {
          connected: 'Conectado', disconnected: 'Desconectado', online: 'Conectado', offline: 'Desconectado',
          available: 'Disponible', busy: 'Ocupado', occupied: 'En llamada', 'in-call': 'En llamada', incall: 'En llamada',
          away: 'Ausente', paused: 'En pausa', break: 'Descanso', ringing: 'Timbrando', dialing: 'Marcando', hold: 'En espera',
        },
      },
      occupancy: {
        title: 'Ocupación de ejecutivos',
        hint: 'Tasa de ocupación diaria (%) por ejecutivo',
        date: 'Fecha',
      },
    },
    sla: {
      asaTargetLabel: 'Objetivo ASA',
      callsLabel: 'Llamadas',
      appointmentsLabel: 'Citas',
      aht: { title: 'AHT', hint: 'Tiempo Medio de Gestión - promedio de gestión entre llamadas y citas' },
      frt: { title: 'FRT', hint: 'Tiempo de Primera Respuesta - espera promedio antes de la primera respuesta' },
      asa: { title: 'ASA', hint: 'Velocidad Media de Atención - porcentaje de interacciones atendidas dentro del objetivo', avg: 'Duración prom.' },
      abandon: { title: 'Tasa de abandono', hint: 'Porcentaje de llamadas en cola abandonadas' },
      surveys: {
        nps: 'NPS', npsHint: 'Promedio de Net Promoter Score',
        ces: 'CES', cesHint: 'Promedio de Customer Effort Score',
        csat: 'CSAT', csatHint: 'Promedio de satisfacción del cliente',
        range: 'Rango', rangeHint: 'Promedio de campos numéricos de rango',
      },
    },
    reports: {
      title: 'Reportes globales',
      hint: 'Reportes agregados de contact-center con agrupación opcional',
      tabs: { calls: 'Llamadas y filas', appointments: 'Citas', agents: 'Agentes' },
      modes: {
        global: 'Global', segment: 'Por segmento', service: 'Por calendario', time: 'Por tiempo',
      },
      groupBy: {
        hour: 'Hora', dayOfWeek: 'Día de la semana', dayOfYear: 'Día del año', date: 'Fecha', month: 'Mes', year: 'Año',
      },
      columns: {
        totalCalls: 'Total de llamadas', totalAppointments: 'Total de citas', avgWaitingTime: 'Tiempo de espera prom.',
        dismissed: 'Descartadas', canceled: 'Canceladas', attended: 'Atendidas', pending: 'Pendientes', rescheduled: 'Reagendadas',
        answered: 'Contestadas', transferred: 'Transferidas', abandoned: 'Abandonadas', rejected: 'Rechazadas', dropped: 'Desconectadas',
        totalQueues: 'Total de filas',
        avgDuration: 'Duración prom.', totalMinutesInCall: 'Min. totales en llamadas', totalMinutesInAppointment: 'Min. totales en citas',
        attendanceRate: 'Tasa de asistencia', tmoCall: 'TMO en llamadas', tmoAppointment: 'TMO en citas', user: 'Ejecutivo',
        segment: 'Segmento', service: 'Calendario',
        hour: 'Hora', date: 'Fecha', year: 'Año', dayOfWeek: 'Día de la semana', month: 'Mes', dayOfYear: 'Día',
      },
    },
    appointments: {
      filters: { agent: 'Filtrar por agente', service: 'Filtrar por calendario' },
      attendanceRate: 'Tasa de asistencia',
      statuses: {
        pending: 'Pendientes', attended: 'Asistidas', canceled: 'Canceladas', rescheduled: 'Reagendadas',
      },
      volume: {
        label: 'Volumen de citas',
        hint: 'Cruce de citas pendientes, asistidas, canceladas y reagendadas según la agrupación seleccionada',
      },
      distribution: {
        label: 'Distribución por estado',
        hint: 'Proporción de citas por estado en el periodo seleccionado',
      },
    },
    maxRange: 'El rango máximo permitido es de 31 días',
    overview: {
      occupancy: { title: 'Ocupación global', hint: 'Tasa de ocupación promedio (%) de todos los ejecutivos' },
      surveysLabel: 'Encuestas',
      topAgents: { title: 'Top agentes por llamadas atendidas', hint: 'Agentes ordenados por llamadas atendidas en el período' },
      topSegments: { title: 'Top segmentos por volumen', hint: 'Segmentos ordenados por llamadas atendidas en el período' },
      sections: { sla: 'SLA', activity: 'Ranking', surveys: 'Encuestas' },
      occupancyLimit: 'Máx. 31 días',
    },
    dayOfWeek: {
      label: 'Agrupar por días de la semana',
    },
    dayOfYear: {
      label: 'Días del año',
    },
    hour: {
      label: 'Agrupar por horas',
    },
    month: {
      label: 'Agrupar por mes',
    },
    year: {
      label: 'Agrupar por año',
    },
    surveys: {
      totalByOption: 'Total por opción',
      totalByOptionHint: 'Número total de veces que se seleccionó una opción',
      averageHint: 'Promedio de todos los valores',
      exports: 'Exportar encuestas',
      exportHint: 'La exportación se ha movido al menú de Exportación unificada',
      rememberMessage: 'Recordatorio',
      rememberDescription: 'Debes seleccionar una encuesta y un campo en los filtros',
    },
    exports: {
      title: 'Exportaciones a XLSX',
      formId: 'ID del formulario',
      appointmentId: 'ID de la cita',
      callId: 'ID de la llamada',
      emptyExport: 'No se encontraron formularios',
      preForm: 'Previo a la llamada',
      contact: 'Contacto',
      alertDates: 'La exportación se realizará en las fechas seleccionadas arriba',
      alertDownload: 'Descargaremos todos los envíos que coincidan con los filtros y el formulario seleccionado',
      entityPlaceholder: 'Selecciona los datos a exportar',
      exportTitle: 'Exportar datos',
      securityAlert: 'Por razones de seguridad, solo es posible extraer 100.000 registros por exportación',
      unitMesuarement: 'La unidad de medida estándar es minutos',
      timezoneAlert: 'Los horarios están en la zona horaria local {timezone}',
      selectFormAgent: 'Selecciona un formulario de agente para exportar',
      confirmAlert: 'Si deseas exportar los formularios de agentes entre las fechas {startAt} y {endAt} haz clic abajo'
    },
    recordings: {
      recordingId: 'ID de la grabación',
      size: 'Tamaño (MB)',
      fileId: 'ID del archivo',
      externalStorage: 'Almacenamiento externo',
      viewer: 'Visor de grabación',
      statusChangeTitle: 'Cambiar estado de la grabación',
      successfulDeleted: 'La grabación se eliminó exitosamente',
      successfulStatus: 'El estado se cambió exitosamente',
      successfulDownload: 'Descarga exitosa',
      hot: 'Almacenamiento en caliente',
      cold: 'Almacenamiento en frío',
      used: 'Usado',
      free: 'Libre',
    },
    topics: {
      titleModal: 'Editando tópicos',
      placeholder: 'Ayúdanos a mejorar, indícanos porqué has agregado o eliminado ciertos tópicos',
    }
  },
  fr: {
    avg: 'Moyenne',
    sum: 'Somme',
    total: 'Total',
    filterHint: 'Vous pouvez filtrer les métriques pour une vue plus granulée.',
    segmentPlaceholder: 'Filtrer par segment',
    agentPlaceholder: 'Filtrer par agent',
    tagsPlaceholder: 'Filtrer par étiquettes',
    formPlaceholder: 'Filtrer par formulaire',
    formTypePlaceholder: 'Filtrer par type de formulaire',
    emptyPeriod: 'La période sélectionnée ne contient pas de données',
    others: 'Autres ({count})',
    entries: 'entrées',
    rangeLimit: 'Cette vue est limitée à {days} jours. Réduisez la plage de dates sélectionnée pour charger les données.',
    indicators: {
      title: 'Indicateurs',
      subtitle: 'Comparez chaque métrique à son objectif',
      panel: 'Panneau',
      manage: 'Gestion',
      condition: 'Condition',
      recommendedWarning: 'Certains indicateurs recommandés ne sont pas encore créés - créez-les pour les suivre :',
      threshold: 'Seuil',
      trend: 'Tendance',
      noHistory: 'Pas encore d\'historique - il se remplira avec le snapshot quotidien.',
      actualSeries: 'Actuel',
      targetSeries: 'Objectif',
      new: 'Nouvel indicateur',
      edit: 'Modifier indicateur',
      empty: 'Aucun indicateur',
      emptyHint: 'Créez un indicateur pour comparer une métrique à son objectif.',
      name: 'Nom',
      metric: 'Métrique',
      operator: 'Condition',
      targetLabel: 'Objectif',
      filtersLabel: 'Filtres (optionnel)',
      segments: 'Segments',
      services: 'Calendriers',
      agents: 'Agents',
      enabled: 'Actif',
      actual: 'Actuel',
      met: 'Dans la cible',
      notMet: 'Hors cible',
      deviation: 'Écart',
      deleteConfirm: 'Supprimer cet indicateur ?',
      saved: 'Indicateur enregistré',
      removed: 'Indicateur supprimé',
      operators: { lt: 'inférieur à', lte: 'au plus', gt: 'supérieur à', gte: 'au moins', eq: 'égal à', between: 'entre' },
    },
    emptyTitle: 'Aucune donnée trouvée',
    queueDuration: 'Temps dans la file d\'attente',
    emptyComments: 'Aucun commentaire',
    aproxLocation: 'Localisation approximative',
    errorCallPreviewMessage: 'L\'appel est en cours',
    errorCallPreviewDescription: 'Vous devez attendre que l\'appel soit répondu par un agent avant de le visualiser',
    formType: 'Type de formulaire',
    globalSearch: 'Recherche globale',
    available: 'Disponible',
    selected: 'Choisi',
    calls: {
      title: 'Durée moyenne des appels',
      hint: 'Voir la durée moyenne des appels dans la période sélectionnée',
      answeredTitle: 'Appels répondus',
      answeredHint: 'Nombre total d\'appels répondus dans la période sélectionnée',
      abandonedTitle: 'Appels abandonnés',
      abandonedHint: 'Nombre total d\'appels abandonnés dans la période sélectionnée',
      transferredTitle: 'Appels transférés',
      transferredHint: 'Nombre total d\'appels transférés dans la période sélectionnée',
      rejectedTitle: 'Appels rejetés',
      rejectedHint: 'Nombre total d\'appels rejetés par chaque agent dans la période sélectionnée',
      unansweredTitle: 'Attributions sans réponse',
      unansweredHint: 'Nombre de fois où chaque agent n\'a pas répondu à un appel attribué avant que l\'ACD ne réassigne, dans la période sélectionnée',
      answeredShare: 'Sur le total des appels répondus par l\'équipe sur la période, la part traitée par cet agent.',
      transferredShare: 'Sur le total des transferts de l\'équipe sur la période, la part réalisée par cet agent.',
      rejectedShare: 'Sur le total des rejets de l\'équipe sur la période, la part réalisée par cet agent.',
      unansweredShare: 'Sur le total des attributions sans réponse de l\'équipe sur la période, la part de cet agent.',
      avgMinutesTitle: 'Minutes moyennes',
      avgMinutesHint: 'Voir la durée moyenne des appels par segment',
      surveyPlaceholder: 'Sélectionner une enquête',
      surveyFieldPlaceholder: 'Sélectionner un champ de l\'enquête',
      hour: {
        label: 'Volume horaire',
        hint: 'Recoupement des appels répondus, abandonnés, transférés et rejetés',
      },
      week: {
        label: 'Volume hebdomadaire',
        hint: 'Recoupement des appels répondus, abandonnés, transférés et rejetés',
      },
      month: {
        label: 'Volume mensuel',
        hint: 'Recoupement des appels répondus, abandonnés, transférés et rejetés',
      },
      distribution: {
        label: 'Répartition par statut',
        hint: 'Proportion des appels par statut dans la période sélectionnée',
      }
    },
    queues: {
      title: 'Durée moyenne dans la file d\'attente',
      hint: 'Voir la durée moyenne d\'attente dans la période sélectionnée',
    },
    agents: {
      metricsTitle: 'Métriques par agent',
      metricsHint: 'AHT, occupation et temps par statut de chaque agent',
      avg: {
        label: 'Durée moyenne par agent',
        hint: 'Vérifiez la durée moyenne des appels par agent',
      },
      survey: {
        label: 'Score du sondage par agent',
        hint: 'Obtenir le score par agent basé sur un champ du sondage'
      },
      aht: {
        title: 'Temps moyen de traitement par agent',
        hint: 'Minutes moyennes d\'appel traitées par agent',
        agent: 'Agent',
        avgMinutes: 'Min. moyennes',
      },
      status: {
        title: 'Temps par statut par agent',
        hint: 'Minutes passées par chaque agent dans chaque statut',
        agent: 'Agent',
        values: {
          connected: 'Connecté', disconnected: 'Déconnecté', online: 'Connecté', offline: 'Déconnecté',
          available: 'Disponible', busy: 'Occupé', occupied: 'En appel', 'in-call': 'En appel', incall: 'En appel',
          away: 'Absent', paused: 'En pause', break: 'Pause', ringing: 'Sonnerie', dialing: 'Numérotation', hold: 'En attente',
        },
      },
      occupancy: {
        title: 'Occupation des agents',
        hint: 'Taux d\'occupation quotidien (%) par agent',
        date: 'Date',
      },
    },
    sla: {
      asaTargetLabel: 'Objectif ASA',
      callsLabel: 'Appels',
      appointmentsLabel: 'Rendez-vous',
      aht: { title: 'AHT', hint: 'Temps Moyen de Traitement - moyenne entre appels et rendez-vous' },
      frt: { title: 'FRT', hint: 'Temps de Première Réponse - attente moyenne avant la première réponse' },
      asa: { title: 'ASA', hint: 'Vitesse Moyenne de Réponse - pourcentage d\'interactions traitées dans l\'objectif', avg: 'Durée moy.' },
      abandon: { title: 'Taux d\'abandon', hint: 'Pourcentage d\'appels en file abandonnés' },
      surveys: {
        nps: 'NPS', npsHint: 'Moyenne du Net Promoter Score',
        ces: 'CES', cesHint: 'Moyenne du Customer Effort Score',
        csat: 'CSAT', csatHint: 'Moyenne de satisfaction client',
        range: 'Plage', rangeHint: 'Moyenne des champs numériques de plage',
      },
    },
    reports: {
      title: 'Rapports globaux',
      hint: 'Rapports agrégés du centre de contact avec regroupement optionnel',
      tabs: { calls: 'Appels et files', appointments: 'Rendez-vous', agents: 'Agents' },
      modes: {
        global: 'Global', segment: 'Par segment', service: 'Par calendrier', time: 'Par temps',
      },
      groupBy: {
        hour: 'Heure', dayOfWeek: 'Jour de la semaine', dayOfYear: 'Jour de l\'année', date: 'Date', month: 'Mois', year: 'Année',
      },
      columns: {
        totalCalls: 'Total des appels', totalAppointments: 'Total des rendez-vous', avgWaitingTime: 'Temps d\'attente moy.',
        dismissed: 'Rejetés', canceled: 'Annulés', attended: 'Traités', pending: 'En attente', rescheduled: 'Reprogrammés',
        answered: 'Répondus', transferred: 'Transférés', abandoned: 'Abandonnés', rejected: 'Rejetés', dropped: 'Déconnectés',
        totalQueues: 'Total des files',
        avgDuration: 'Durée moy.', totalMinutesInCall: 'Min. totales en appels', totalMinutesInAppointment: 'Min. totales en rendez-vous',
        attendanceRate: 'Taux de présence', tmoCall: 'TMO en appels', tmoAppointment: 'TMO en rendez-vous', user: 'Agent',
        segment: 'Segment', service: 'Calendrier',
        hour: 'Heure', date: 'Date', year: 'Année', dayOfWeek: 'Jour de la semaine', month: 'Mois', dayOfYear: 'Jour',
      },
    },
    appointments: {
      filters: { agent: 'Filtrer par agent', service: 'Filtrer par calendrier' },
      attendanceRate: 'Taux de présence',
      statuses: {
        pending: 'En attente', attended: 'Traités', canceled: 'Annulés', rescheduled: 'Reprogrammés',
      },
      volume: {
        label: 'Volume de rendez-vous',
        hint: 'Recoupement des rendez-vous en attente, traités, annulés et reprogrammés selon le regroupement sélectionné',
      },
      distribution: {
        label: 'Répartition par statut',
        hint: 'Proportion des rendez-vous par statut dans la période sélectionnée',
      },
    },
    maxRange: 'La plage maximale autorisée est de 31 jours',
    overview: {
      occupancy: { title: 'Occupation globale', hint: 'Taux d\'occupation moyen (%) de tous les agents' },
      surveysLabel: 'Enquêtes',
      topAgents: { title: 'Top agents par appels répondus', hint: 'Agents classés par appels répondus sur la période' },
      topSegments: { title: 'Top segments par volume', hint: 'Segments classés par appels répondus sur la période' },
      sections: { sla: 'SLA', activity: 'Classement', surveys: 'Enquêtes' },
      occupancyLimit: 'Max 31 jours',
    },
    dayOfWeek: {
      label: 'Grouper par jours de la semaine',
    },
    dayOfYear: {
      label: 'Jours de l\'année',
    },
    hour: {
      label: 'Grouper par heures',
    },
    month: {
      label: 'Grouper par mois',
    },
    year: {
      label: 'Grouper par année',
    },
    surveys: {
      totalByOption: 'Total par option',
      totalByOptionHint: 'Nombre total de fois qu\'une option a été sélectionnée',
      averageHint: 'Moyenne de toutes les valeurs',
      exports: 'Exporter les enquêtes',
      exportHint: 'L\'exportation de l\'enquête a été déplacée vers le menu Exporter',
      rememberMessage: 'Rappel',
      rememberDescription: 'Vous devez sélectionner une enquête et un champ dans les filtres',
    },
    exports: {
      title: 'Exports vers XLSX',
      formId: 'ID du formulaire',
      appointmentId: 'ID du rendez-vous',
      callId: 'ID de l\'appel',
      emptyExport: 'Aucun formulaire trouvé',
      preForm: 'Avant l\'appel',
      contact: 'Contact',
      alertDates: 'L\'exportation sera effectuée sur les dates sélectionnées ci-dessus',
      alertDownload: 'Nous téléchargerons tous les envois correspondant aux filtres et au formulaire sélectionné',
      entityPlaceholder: 'Sélectionner les données à exporter',
      exportTitle: 'Exporter les données',
      securityAlert: 'Pour des raisons de sécurité, il est seulement possible d\'extraire 100.000 enregistrements par exportation',
      unitMesuarement: 'L\'unité de mesure standard est les minutes',
      timezoneAlert: 'Les heures sont dans le fuseau horaire local {timezone}',
      selectFormAgent: 'Sélectionner un formulaire d\'agent à exporter',
      confirmAlert: 'Si vous souhaitez exporter les formulaires d\'agent entre les dates {startAt} et {endAt}, cliquez ci-dessous',
    },
    recordings: {
      recordingId: 'ID de l\'enregistrement',
      size: 'Taille (MB)',
      fileId: 'ID du fichier',
      externalStorage: 'Stockage externe',
      viewer: 'Visionneur d\'enregistrement',
      statusChangeTitle: 'Changer le statut de l\'enregistrement',
      successfulDeleted: 'L\'enregistrement a été supprimé avec succès',
      successfulStatus: 'Le statut a été changé avec succès',
      successfulDownload: 'Téléchargement réussi',
      used: 'Utilisé',
      free: 'Libre',
      hot: 'Stockage chaud',
      cold: 'Stockage froid',
    },
    topics: {
      titleModal: 'Modification des sujets',
      placeholder: 'Aidez-nous à nous améliorer, dites-nous pourquoi vous avez ajouté ou supprimé certains sujets',
    }
}

};
